import { useTranslation } from "react-i18next";
import { useEffect, useRef, type MouseEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { isModifiedClick } from "@/lib/link-utils";
import { useComparePricingPlan } from "@/api/hooks/use-subscription";
import { usePaymentCore } from "@/features/payment";
import {
  ProfileTab,
  BillingTab,
  SecurityTab,
  AppearanceTab,
  PermissionsTab,
  RewardsTab,
} from "@/features/settings/components";
import { UsageTab } from "@/features/settings/usage/UsageTab";
import {
  useSettingsStore,
  type SettingsTab,
} from "@/features/settings/store";
import useModalStore from "@/store/use-modal-store";
import { usePageTitle } from "@/store/use-page-title-store";
import { BackButton } from "@/components/ui/back-button";

const VALID_TABS: SettingsTab[] = [
  "profile",
  "security",
  "appearance",
  "billing",
  "usage",
  "permissions",
  "rewards",
];

const SettingsPage = () => {
  const { t } = useTranslation();
  const { tab: urlTab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  const activeTab: SettingsTab =
    VALID_TABS.includes(urlTab as SettingsTab)
      ? (urlTab as SettingsTab)
      : "profile";

  const { unsavedTabs, hasUnsavedChanges, clearAllDrafts, markTabAsSaved, setActiveTab } =
    useSettingsStore();
  const { setPageTitle, clearPageTitle } = usePageTitle();
  const { comparePricingPlan } = useComparePricingPlan();
  const { openSubscriptionChangeModal } = usePaymentCore();
  const [, setSearchParams] = useSearchParams();
  const hasCheckedPlanChange = useRef(false);

  // Store'u URL ile senkronize et (store'dan okuyan diğer componentler için)
  useEffect(() => {
    setActiveTab(activeTab);
  }, [activeTab, setActiveTab]);

  const handleTabClick = (
    event: MouseEvent<HTMLAnchorElement>,
    tab: SettingsTab,
  ) => {
    if (isModifiedClick(event)) return; // yeni sekmede açılıyor, mevcut sayfa değişmiyor
    if (hasUnsavedChanges) {
      event.preventDefault(); // Link'in navigasyonunu iptal et, onay sonrası manuel navigate edilecek
      useModalStore.getState().openUnsavedChanges(() => {
        clearAllDrafts();
        markTabAsSaved(tab);
        navigate(`/settings/${tab}`);
      });
    }
  };

  // Set page title for mobile header
  useEffect(() => {
    setPageTitle("settings.title", "settings.description");

    // Cleanup on unmount
    return () => clearPageTitle();
  }, [setPageTitle, clearPageTitle]);

  // On entering the settings page, check once whether the user's subscription
  // plan changed. If it was upgraded/downgraded, surface the same success modal
  // used after checkout with the matching message.
  useEffect(() => {
    if (hasCheckedPlanChange.current) return;
    hasCheckedPlanChange.current = true;

    comparePricingPlan()
      .then((response) => {
        const type = response?.data?.type;
        if (type === "upgrade" || type === "downgrade") {
          openSubscriptionChangeModal(type);
          // Reflect the plan change in the URL (preserve any existing params)
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("subscription_update_action", type);
              return next;
            },
            { replace: true }
          );
        }
      })
      .catch((error) => {
        console.error("Compare pricing plan check failed:", error);
      });
  }, [comparePricingPlan, openSubscriptionChangeModal, setSearchParams]);
  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return <ProfileTab />;
      case "security":
        return <SecurityTab />;
      case "appearance":
        return <AppearanceTab />;
      case "billing":
        return <BillingTab />;
      case "usage":
        return <UsageTab />;
      case "permissions":
        return <PermissionsTab />;
      case "rewards":
        return <RewardsTab />;
      default:
        return <ProfileTab />;
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary">
      {/* Header Section - Responsive: mobile-first, desktop Figma exact with */}

      <div className="hidden md:block w-full border-b border-border-secondary dark:border-border-secondary sticky -top-1 z-10 bg-bg-primary dark:bg-bg-primary">
        <div className="pt-4 md:pt-6 pb-4 md:pb-6 max-w-screen-xl px-4 md:px-8 mx-auto">
          <div className="flex flex-col gap-4">
            {/* Title & Description */}
            <div className="hidden md:block flex-1 min-w-0 justify-between">
              <div className="flex items-center gap-2">
                <BackButton />
                <h1 className="text-xl md:text-2xl font-semibold text-text-primary dark:text-text-primary leading-7 md:leading-8 mb-1">
                  {t("settings.title")}
                </h1>
              </div>
              <p className="ml-8 text-sm md:text-base text-text-tertiary dark:text-text-tertiary leading-5 md:leading-6">
                {t("settings.description")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Figma Layout: Full width with proper spacing */}
      <div className="px-4 md:px-[46px] py-4 md:py-6 bg-red-">
        <div className="w-full max-w-screen-2xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-24">
            {/* Vertical Tabs - Sidebar - Figma: 111px width */}
            <div className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0  shrink-0">
              {(
                [
                  "profile",
                  "security",
                  "appearance",
                  "billing",
                  "usage",
                  "permissions",
                  "rewards",
                ] as SettingsTab[]
              ).map((tab) => (
                <Link
                  key={tab}
                  to={`/settings/${tab}`}
                  onClick={(event) => handleTabClick(event, tab)}
                  className={cn(
                    "flex items-center px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors lg:w-full relative",
                    activeTab === tab
                      ? "bg-bg-secondary dark:bg-bg-secondary text-text-secondary dark:text-text-secondary"
                      : "text-text-quaternary dark:text-text-quaternary hover:bg-bg-tertiary dark:hover:bg-bg-tertiary",
                  )}
                >
                  {t(`settings.tabs.${tab}`)}
                  {/* Unsaved Changes Indicator */}
                  {unsavedTabs.has(tab) && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full" />
                  )}
                </Link>
              ))}
            </div>

            {/* Content Section - Figma: flex-1 with proper width */}
            <div className="flex-1 min-w-0 bg-bg-secondary dark:bg-bg-secondary rounded-2xl py-4 px-3 sm:py-6 sm:px-4 lg:p-8">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
